/**
 * Headless AI: leverage the deterministic engine by sampling candidate shots,
 * simulating each, and scoring the outcome with the rules evaluator. The chosen
 * shot is then run through a make-rate GOVERNOR so the opponent's long-run
 * average (points per inning) lands in the band documented for its rank — while
 * the shot SELECTION visibly reflects the tier's repertoire (see ai/skill.ts).
 * No rendering — runs entirely on the physics module.
 */
import { type BallId } from '../config';
import { simulate, type SimOptions, type SimulationResult, type World } from '../physics';
import { evaluateEventLog, type CaromRuleConfig } from '../rules';
import type { BallSnapshot, CueParams } from '../types';
import { skillForLevel, type SkillProfile } from './skill';

// Re-exported for callers that still think in coarse tiers.
export type Difficulty = 'easy' | 'medium' | 'hard';
const TIER_LEVEL: Record<Difficulty, number> = { easy: 60, medium: 175, hard: 350 };

// Approximate sim used while SEARCHING: a 2 ms step (still well under a ball
// radius per step) and sparse frames roughly halve the search cost. Good enough to
// RANK hundreds of candidates — but not to decide what to play, since it disagrees
// with the engine's real 1 ms solve on ~17% of makes (see VERIFY_SIM).
const AI_SIM: SimOptions = { dt: 2 / 1000, frameDt: 1 / 60 };
// The shot actually played is confirmed at the engine's DEFAULT timestep — the
// same physics the renderer will replay it with — so a "found make" the search
// commits to is a make on the table, not only in the approximation.
const VERIFY_SIM: SimOptions | undefined = undefined;
// Aim-error multiples (of hitNoise) tried for a deliberate miss, smallest first.
const MISS_LADDER = [3, 6, 12, 24];
// See BANK_LEAVE_PENALTY in server/netshot.mjs: a cushion-first make needs a
// clearly better leave than a straight hit to win the position-play tie-break —
// otherwise a marginally better leave lets the AI pass on an easy hanger for a
// bank, which reads as an obviously wrong shot choice from the rail.
const BANK_LEAVE_PENALTY = 0.3;

const clamp = (x: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, x));
const SCORE_MAKE = 1000;

/**
 * Pick the computer's shot for the given handicap/level.
 *
 * `rule` is the rule the AI should PLAY TO, which is not always the match's rule:
 * in the 마지막 3쿠션 final stage the caller raises `minCushions` to
 * `finalShotMinCushions`, because a plain carom there scores nothing AND gives up
 * the table (see rules/match.ts), so it must not count as a "make".
 *
 * @param level the AI's Korean handicap (점) — also its skill rank. Accepts the
 *   legacy 'easy' | 'medium' | 'hard' tiers for back-compat.
 */
export function findBestShot(
  base: World,
  cueBall: BallId,
  rule: CaromRuleConfig,
  isBreakShot: boolean,
  level: number | Difficulty
): CueParams {
  const lvl = typeof level === 'number' ? level : TIER_LEVEL[level];
  const cfg = skillForLevel(lvl);
  const { cue: best, score: bestScore, nearMiss } = searchBest(base, cueBall, rule, isBreakShot, cfg);

  /**
   * Draw an execution-noise stroke around `base` and keep the first one that still
   * does what the governor INTENDED, confirmed against the physics the renderer
   * will actually replay. The stroke used to be applied after the search and played
   * unchecked, which is how a chosen carom became a foul on the table. Every
   * non-fouling draw is remembered in `floor` as a legality fallback.
   */
  let floor: CueParams | null = null;
  const stroke = (from: CueParams, aim: number, scale: number, want: 'score' | 'miss', tries = 4): CueParams | null => {
    for (let i = 0; i < tries; i++) {
      const cand = perturb(from, cfg, aim, scale);
      const ev = evaluateEventLog(simulate(base, cand, VERIFY_SIM).events, cueBall, rule, isBreakShot);
      if (ev.foul) continue; // a foul is never what the governor asked for
      floor = cand;
      if (want === 'score' ? ev.scored : !ev.scored) return cand;
    }
    return null;
  };

  // make-rate governor: commit the make cleanly with probability p (boosted by
  // cfg.commitProbability from the raw target rate — see skill.ts) or play a
  // DELIBERATE miss otherwise, so the realized average matches the rank. If the
  // search found no make at all (a genuinely hard leave), just play the best
  // legal attempt.
  const foundMake = bestScore >= SCORE_MAKE;
  let cue: CueParams | null = null;
  if (foundMake && Math.random() < cfg.commitProbability) {
    // the governor decided this shot scores, so don't let stroke noise waste it
    cue = stroke(best, cfg.hitNoise, 0.25, 'score') ?? stroke(best, cfg.hitNoise * 0.3, 0.1, 'score', 2);
  } else if (foundMake) {
    // A player who misses misses THE SHOT THEY WERE PLAYING — they don't abandon a
    // hanging carom for an unrelated line, which is what committing to a separate
    // near-miss candidate looked like from the rail. Widen the stroke error on the
    // chosen line instead, smallest first, and take the narrowest one that fails.
    for (const mult of MISS_LADDER) {
      cue = stroke(best, cfg.hitNoise * mult, 0.6, 'miss', 2);
      if (cue) break;
    }
    if (!cue && nearMiss) cue = stroke(nearMiss, cfg.hitNoise * 1.5, 0.6, 'miss', 3);
  } else if (nearMiss) {
    cue = stroke(nearMiss, cfg.hitNoise * 1.5, 0.6, 'miss', 3);
  }
  // Legality floor: a stroke draw that at least verified legal, else the best line
  // the search proved doesn't foul. Only the last fallback can foul, and it is
  // reached solely when the search never found a legal shot at all.
  return cue ?? floor ?? nearMiss ?? perturb(best, cfg, cfg.hitNoise * 2, 1);
}

/**
 * The raw search (no execution noise, no governor): find the highest-scoring
 * shot the rank's repertoire + search effort can discover. Exposed for the
 * calibration harness; production goes through findBestShot.
 */
export function searchBest(
  base: World,
  cueBall: BallId,
  rule: CaromRuleConfig,
  isBreakShot: boolean,
  cfg: SkillProfile
): { cue: CueParams; score: number; nearMiss: CueParams | null } {
  const objects = scoringObjects(base, cueBall, rule);
  // Aim seeds: the headings from the cue ball straight at each scoring object.
  // Searching AROUND these (not over a full circle) is what lets the search
  // actually find caroms — a uniform azimuth almost never hits both reds.
  const aimDirs = objectHeadings(base, cueBall, objects);

  // A make carries its own gather score + rest layout so we can look one shot
  // ahead; a miss only carries a partial-progress score for the search gradient.
  const makes: { cue: CueParams; gather: number; leave: Record<BallId, BallSnapshot>; bankFirst: boolean }[] = [];
  let bestMiss = aimDirs.length ? null : ({ ball: cueBall, azimuth: 0, elevation: 0, speed: 2.5, tipOffset: { a: 0, b: 0 } } as CueParams);
  let bestMissScore = -Infinity;

  const consider = (cue: CueParams): number => {
    const res = simulate(base, cue, AI_SIM);
    const ev = evaluateEventLog(res.events, cueBall, rule, isBreakShot);
    if (ev.foul) return -100;
    if (ev.scored) {
      makes.push({
        cue,
        gather: gatherBonus(res, cue, cueBall, objects, cfg.gatherWeight),
        leave: res.finalState,
        bankFirst: (ev.cushionsBeforeFirst ?? 0) >= 1,
      });
      return SCORE_MAKE;
    }
    const s = missScore(res, ev, cueBall, rule, objects);
    if (s > bestMissScore) {
      bestMissScore = s;
      bestMiss = cue;
    }
    return s;
  };

  // 1) structured fan: dense cut-angle × speed grid around each object ball.
  const candidates = buildCandidates(base, cueBall, cfg, aimDirs);
  let refineSeed = candidates[0];
  let seedScore = consider(refineSeed);
  for (let i = 1; i < candidates.length; i++) {
    const s = consider(candidates[i]);
    if (s > seedScore) {
      seedScore = s;
      refineSeed = candidates[i];
    }
  }

  // 2) local refinement: hill-climb around the best so far with a shrinking
  //    radius — turns a near-miss line into a clean bank/system (뒤돌려치기) the
  //    beginner tiers never search far enough to find.
  let radAz = 0.22;
  let radSp = 0.8;
  for (let r = 0; r < cfg.refineRounds; r++) {
    let improved = false;
    for (let i = 0; i < 24; i++) {
      const cand = jitterCue(refineSeed, cfg, radAz, radSp);
      const s = consider(cand);
      if (s > seedScore) {
        seedScore = s;
        refineSeed = cand;
        improved = true;
      }
    }
    radAz *= 0.5;
    radSp *= 0.5;
    if (!improved) break;
  }

  const nearMiss = bestMissScore > 0 ? bestMiss : null;
  if (makes.length === 0) return { cue: bestMiss ?? refineSeed, score: bestMissScore, nearMiss };

  // 3) position play: among the best gathering makes, prefer the one whose LEAVE
  //    keeps the run alive — i.e. the next carom is still on. This look-ahead is
  //    what lets higher ranks string long runs (모아치기/가락). Beginners (K=0)
  //    take the FIRST-INSTINCT make instead: the line closest to straight at the
  //    nearest ball — they pick the shot they see, not the best one available.
  makes.sort((a, b) => b.gather - a.gather);
  if (cfg.lookaheadK <= 0) {
    let pick = makes[0];
    if (cfg.naiveBias > 0.3 && makes.length > 1) {
      const straight = nearestObjectHeading(base, cueBall, objects);
      const off = (c: CueParams) => Math.abs(angleDiff(c.azimuth, straight));
      pick = makes.reduce((a, b) => (off(b.cue) < off(a.cue) ? b : a));
    }
    return { cue: pick.cue, score: SCORE_MAKE + pick.gather, nearMiss };
  }

  let chosen = makes[0];
  let bestLeave = -Infinity;
  for (const m of makes.slice(0, cfg.lookaheadK)) {
    const q = leaveQuality(base, m.leave, cueBall, rule, cfg, objects) + m.gather * 0.01 - (m.bankFirst ? BANK_LEAVE_PENALTY : 0);
    if (q > bestLeave) {
      bestLeave = q;
      chosen = m;
    }
  }
  return { cue: chosen.cue, score: SCORE_MAKE + chosen.gather, nearMiss };
}

/**
 * One-shot look-ahead: from a make's rest layout, can the AI still score? Returns
 * a value > 1 (weighted by how well that next shot itself gathers) when a make
 * exists, else 0 — so the search steers toward leaves that keep the run going.
 */
function leaveQuality(
  base: World,
  leave: Record<BallId, BallSnapshot>,
  cueBall: BallId,
  rule: CaromRuleConfig,
  cfg: SkillProfile,
  objects: BallId[]
): number {
  const w = base.clone();
  for (const b of w.balls) {
    const s = leave[b.id];
    if (!s) continue;
    b.pos = { ...s.pos };
    b.vel = { x: 0, y: 0, z: 0 };
    b.angVel = { x: 0, y: 0, z: 0 };
    b.state = 'stationary';
  }
  const aimDirs = objectHeadings(w, cueBall, objects);
  // a small, cheap fan — just enough to tell whether a carom is available
  const probe: SkillProfile = { ...cfg, samples: 36, refineRounds: 0, sideSpin: Math.min(cfg.sideSpin, 0.2) };
  const cands = buildCandidates(w, cueBall, probe, aimDirs);
  let best = 0;
  for (const c of cands) {
    const res = simulate(w, c, AI_SIM);
    const ev = evaluateEventLog(res.events, cueBall, rule, false);
    if (ev.scored) {
      best = Math.max(best, 1 + gatherBonus(res, c, cueBall, objects, cfg.gatherWeight) * 0.04);
    }
  }
  return best;
}

/** Partial-progress score for a miss, so the search still has a gradient. */
function missScore(
  res: SimulationResult,
  ev: ReturnType<typeof evaluateEventLog>,
  cueBall: BallId,
  rule: CaromRuleConfig,
  objects: BallId[]
): number {
  let s = 0;
  if (ev.firstObjectBall) s += 10;
  if (ev.bothContacted) s += 40;
  s += Math.min(ev.cushionsBeforeSecond, rule.minCushions) * 8;
  s += proximityBonus(res, cueBall, ev.firstObjectBall, objects);
  return s;
}

/**
 * Build the candidate shot set within the tier's sample budget: a dense
 * cut-angle × speed fan around each scoring object ball (the bulk — this is what
 * finds caroms), plus a handful of free-azimuth banks whose share grows with
 * rank so masters discover system lines (뒤돌려치기). Spin/elevation enter during
 * refinement; a light spin spread is mixed in at ranks that use English.
 */
function buildCandidates(base: World, cueBall: BallId, cfg: SkillProfile, aimDirs: number[]): CueParams[] {
  const out: CueParams[] = [];
  const bankChance = clamp((cfg.level - 80) / 1400, 0.03, 0.3);
  const nBank = Math.round(cfg.samples * bankChance);
  const nFan = Math.max(1, cfg.samples - nBank);

  // distribute the fan budget over the object balls × a speed grid
  const dirs = aimDirs.length ? aimDirs : [0];
  const speeds = Math.max(3, Math.round(Math.sqrt(nFan / dirs.length)));
  const cutsPer = Math.max(6, Math.ceil(nFan / (dirs.length * speeds)));
  const CUT = 0.6; // half-width of the cut fan (rad)
  // The fan is CENTRE-BALL — clean geometry finds caroms most reliably. Spin is
  // layered on only as a small extra slice (and by refinement) so it assists a
  // carom rather than corrupting every candidate's line.
  for (const aim of dirs) {
    for (let ci = 0; ci < cutsPer; ci++) {
      const cut = -CUT + (2 * CUT * ci) / (cutsPer - 1 || 1);
      for (let si = 0; si < speeds; si++) {
        const speed = cfg.minSpeed + ((cfg.maxSpeed - cfg.minSpeed) * si) / (speeds - 1 || 1);
        out.push({ ball: cueBall, azimuth: aim + cut, elevation: 0, speed, tipOffset: { a: 0, b: 0 } });
        // one spin-assisted variant per cut at ranks that use English
        if (cfg.sideSpin > 0 && si === speeds - 1) {
          const a = (Math.random() * 2 - 1) * cfg.sideSpin;
          const b = (Math.random() * 2 - 1) * cfg.topDraw * 0.6;
          out.push({ ball: cueBall, azimuth: aim + cut, elevation: 0, speed, tipOffset: { a, b } });
        }
      }
    }
  }

  // free-azimuth banks (system shots) — random, with the tier's full repertoire
  for (let i = 0; i < nBank; i++) {
    const elevation = cfg.maxElevation > 0 && Math.random() < 0.2 ? Math.random() * cfg.maxElevation : 0;
    out.push({
      ball: cueBall,
      azimuth: Math.random() * Math.PI * 2,
      elevation,
      speed: cfg.minSpeed + Math.random() * (cfg.maxSpeed - cfg.minSpeed),
      tipOffset: { a: (Math.random() * 2 - 1) * cfg.sideSpin, b: (Math.random() * 2 - 1) * cfg.topDraw },
    });
  }
  return out;
}

/** Smallest signed angle difference (rad). */
function angleDiff(a: number, b: number): number {
  let d = (a - b) % (Math.PI * 2);
  if (d > Math.PI) d -= Math.PI * 2;
  if (d < -Math.PI) d += Math.PI * 2;
  return d;
}

/** Heading from the cue ball straight at its NEAREST object ball. */
function nearestObjectHeading(base: World, cueBall: BallId, objects: BallId[]): number {
  const cp = base.get(cueBall).pos;
  let best = 0;
  let bestD = Infinity;
  for (const o of objects) {
    const op = base.get(o).pos;
    const d = Math.hypot(op.x - cp.x, op.y - cp.y);
    if (d < bestD) {
      bestD = d;
      best = Math.atan2(op.y - cp.y, op.x - cp.x);
    }
  }
  return best;
}

/** Headings (rad) from the cue ball straight at each scoring object ball. */
function objectHeadings(base: World, cueBall: BallId, objects: BallId[]): number[] {
  const cp = base.get(cueBall).pos;
  return objects.map((o) => {
    const op = base.get(o).pos;
    return Math.atan2(op.y - cp.y, op.x - cp.x);
  });
}

/** Perturb a shot during hill-climb (search-space jitter, not execution noise). */
function jitterCue(base: CueParams, cfg: SkillProfile, radAz: number, radSp: number): CueParams {
  return {
    ball: base.ball,
    azimuth: base.azimuth + (Math.random() * 2 - 1) * radAz,
    elevation: cfg.maxElevation > 0 ? clamp(base.elevation + (Math.random() * 2 - 1) * 0.04, 0, cfg.maxElevation) : 0,
    speed: clamp(base.speed + (Math.random() * 2 - 1) * radSp, cfg.minSpeed, cfg.maxSpeed),
    tipOffset: {
      a: clamp(base.tipOffset.a + (Math.random() * 2 - 1) * 0.15, -cfg.sideSpin, cfg.sideSpin),
      b: clamp(base.tipOffset.b + (Math.random() * 2 - 1) * 0.1, -cfg.topDraw, cfg.topDraw),
    },
  };
}

/** Apply execution noise to the committed shot (this is the stroke, not search). */
function perturb(cue: CueParams, cfg: SkillProfile, aim: number, scale: number): CueParams {
  return {
    ...cue,
    azimuth: cue.azimuth + (Math.random() * 2 - 1) * aim,
    speed: Math.max(0.8, cue.speed + (Math.random() * 2 - 1) * cfg.speedNoise * scale),
    tipOffset: {
      a: cue.tipOffset.a + (Math.random() * 2 - 1) * cfg.spinNoise * scale,
      b: cue.tipOffset.b + (Math.random() * 2 - 1) * cfg.spinNoise * scale,
    },
  };
}

/** The balls a carom must contact (four-ball: both reds; else every other ball). */
function scoringObjects(base: World, cueBall: BallId, rule: CaromRuleConfig): BallId[] {
  const present = base.balls.map((b) => b.id).filter((b) => b !== cueBall);
  return rule.caromObjectBalls ? rule.caromObjectBalls.filter((b) => present.includes(b)) : present;
}

/**
 * Reward leaving a tight, easy next shot — the essence of 모아치기/가락 and the
 * whole reason higher ranks string runs together. Three ingredients:
 *  - tightest object-ball pair (the two reds close = an easy re-carom),
 *  - cue ball near that cluster, and
 *  - a SOFT shot (low speed barely disturbs the gather).
 * Worth nothing when gatherWeight is 0 (beginners take the carom wherever it
 * leaves them).
 */
function gatherBonus(res: SimulationResult, cue: CueParams, cueBall: BallId, objects: BallId[], weight: number): number {
  if (weight <= 0) return 0;
  const final = res.finalState;
  const reds = objects.filter((id) => final[id]);
  if (reds.length === 0) return 0;
  const cp = final[cueBall].pos;

  // distance between the two reds (tighter is better)
  let redSpread = 0;
  if (reds.length >= 2) {
    const a = final[reds[0]].pos;
    const b = final[reds[1]].pos;
    redSpread = Math.hypot(a.x - b.x, a.y - b.y);
  }
  // cue ball's distance to the nearest red
  let cueDist = Infinity;
  for (const r of reds) {
    const rp = final[r].pos;
    cueDist = Math.min(cueDist, Math.hypot(cp.x - rp.x, cp.y - rp.y));
  }

  const tight = 1 / (1 + redSpread * 6); // steep: rewards the reds ending close
  const near = 1 / (1 + cueDist * 4); // cue left on top of them
  const soft = 1 / (1 + cue.speed * 0.5); // gentle stroke keeps the gather
  return weight * (0.5 * tight + 0.3 * near + 0.2 * soft);
}

/** Reward getting the cue ball close to the object ball(s) it still needs to hit. */
function proximityBonus(res: SimulationResult, cueBall: BallId, firstObj: BallId | null, objects: BallId[]): number {
  if (res.frames.length === 0) return 0;
  const needed = firstObj ? objects.filter((o) => o !== firstObj) : objects;
  let minDist = Infinity;
  for (const f of res.frames) {
    const cp = f.balls[cueBall].pos;
    for (const o of needed) {
      const op = f.balls[o]?.pos;
      if (!op) continue;
      const d = Math.hypot(cp.x - op.x, cp.y - op.y);
      if (d < minDist) minDist = d;
    }
  }
  return minDist < Infinity ? 6 / (1 + minDist * 4) : 0;
}
